import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { EventBus } from '@/lib/event-bus'

// Extended ThemeState to include method signatures with source parameter
interface ExtendedThemeState extends GulfstreamTheme.ThemeState {
  setColorScheme(scheme: GulfstreamTheme.ColorScheme, source?: string): void
  toggleDarkMode(source?: string): void
  setDensity(density: GulfstreamTheme.DensityScheme, source?: string): void
  setRadius(radius: GulfstreamTheme.RadiusSize, source?: string): void
  setComponentRadius(componentRadius: GulfstreamTheme.ComponentRadius, source?: string): void
}

export const useThemeStore = create<ExtendedThemeState>()(
  persist(
    (set) => ({
      // Default values
      colorScheme: 'neutral',
      darkMode: false,
      density: 'comfortable',
      radius: 'medium',
      componentRadius: {},
      
      // Actions
      setColorScheme: (scheme: GulfstreamTheme.ColorScheme, source = 'store') => {
        set({ colorScheme: scheme })
        
        // Publish event for other components to react, only if initiated from store
        if (source === 'store') {
          EventBus.publish({
            type: 'theme:change',
            payload: {
              value: scheme,
              source: 'store'
            }
          })
        }
      },
      
      toggleDarkMode: (source = 'store') => {
        set((state) => {
          const newDarkMode = !state.darkMode
          
          // Publish event for other components to react, only if initiated from store
          if (source === 'store') {
            EventBus.publish({
              type: 'darkMode:toggle',
              payload: {
                value: newDarkMode,
                source: 'store'
              }
            })
          }
          
          return { darkMode: newDarkMode }
        })
      },
      
      setDensity: (density: GulfstreamTheme.DensityScheme, source = 'store') => {
        set({ density })
        
        // Publish event for other components to react, only if initiated from store
        if (source === 'store') {
          EventBus.publish({
            type: 'density:change',
            payload: {
              value: density,
              source: 'store'
            }
          })
        }
      },
      
      setRadius: (radius: GulfstreamTheme.RadiusSize, source = 'store') => {
        set({ radius })
        
        // Publish event for other components to react, only if initiated from store
        if (source === 'store') {
          EventBus.publish({
            type: 'radius:change',
            payload: {
              value: radius,
              source: 'store'
            }
          })
        }
      },
      
      setComponentRadius: (componentRadius: GulfstreamTheme.ComponentRadius, source = 'store') => {
        set((state) => ({
          componentRadius: { ...state.componentRadius, ...componentRadius }
        }))
        
        // Publish event for other components to react, only if initiated from store
        if (source === 'store') {
          EventBus.publish({
            type: 'componentRadius:change',
            payload: {
              value: componentRadius,
              source: 'store'
            }
          })
        }
      }
    }),
    {
      name: 'gulf-theme-storage', // Name for localStorage
      partialize: (state) => ({
        colorScheme: state.colorScheme,
        darkMode: state.darkMode,
        density: state.density,
        radius: state.radius,
        componentRadius: state.componentRadius
      })
    }
  )
)

// Subscribe to theme events from other sources
EventBus.subscribe('theme:change', (payload: any) => {
  // Prevent circular events by checking the source
  if (payload.source !== 'store') {
    useThemeStore.getState().setColorScheme(payload.value, payload.source)
  }
})

EventBus.subscribe('darkMode:toggle', (payload: any) => {
  // Prevent circular events by checking the source
  if (payload.source !== 'store') {
    useThemeStore.getState().toggleDarkMode(payload.source)
  }
})

EventBus.subscribe('density:change', (payload: any) => {
  // Prevent circular events by checking the source
  if (payload.source !== 'store') {
    useThemeStore.getState().setDensity(payload.value, payload.source)
  }
})

EventBus.subscribe('radius:change', (payload: any) => {
  // Prevent circular events by checking the source
  if (payload.source !== 'store') {
    useThemeStore.getState().setRadius(payload.value, payload.source)
  }
})

EventBus.subscribe('componentRadius:change', (payload: any) => {
  // Prevent circular events by checking the source
  if (payload.source !== 'store') {
    useThemeStore.getState().setComponentRadius(payload.value, payload.source)
  }
}) 